import React, { useContext } from 'react';
import { SelectOption } from 'global';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { CommonDataContext } from 'contexts/CommonDataContext';
import { accountSelectors } from 'redux/accountReducer';
import { AccountType, PortalConfigurationType } from '@trnsact/trnsact-shared-types';
import { Grid, Stack, CircularProgress } from '@mui/material';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import {
  useExternalDashboards,
  externalDashboardsApi,
  adminExternalDashboardsApi,
  AdminDashboardQueryResponse,
  AdminDashboardOptionsListResponse,
  DashboardListResponse,
} from 'modules/integrations/externalDashboards';

import { PortalConfigurationRenderProps } from '../../types';
import { ExternalDashboardForm } from './ExternalDashboardForm';
import { getPortalConfigurationDefaultConfig } from '../../lib/getPortalConfigurationDefaultConfig';
import { AccountId, DashboardId, ExternalDashboardsPortalConfigFormValues } from '../types';

export const ExternalDashboardManagement = ({ vendorProfile, onSaveConfiguration }: PortalConfigurationRenderProps) => {
  const { isLenderUser } = useContext(CommonDataContext);
  const accountType = useSelector(accountSelectors.accountType);

  const { handleUpdateAccessByAccount, isUpdatingAccessByAccount } = useExternalDashboards();

  const { data: defaultDashboardData } = useQuery<DashboardListResponse>(externalDashboardsApi.getDashboardList, {
    skip: !accountType || !isLenderUser,
    variables: {
      input: { accountType },
    },
  });

  const defaultDashboardOptions: SelectOption[] = (defaultDashboardData?.externalDashboards?.result?.list ?? []).map(
    item => ({
      label: item.name,
      value: item.id,
    })
  );

  const externalDashboardAccessConfig = useSelector((state: any) =>
    portalConfigurationsSelectors.configDataByType(state, PortalConfigurationType.ExternalDashboardAccess)
  );

  const savedDefaultDashboard = externalDashboardAccessConfig?.jsonDefinition?.config?.defaultDashboard;
  const defaultDashboardEnabled = savedDefaultDashboard?.enabled ?? false;
  const defaultDashboardId = savedDefaultDashboard?.dashboardId ?? '';

  const { data: dashboardsResponse, loading: isDashboardListOptionsLoading } =
    useQuery<AdminDashboardOptionsListResponse>(adminExternalDashboardsApi.getAdminDashboardListOptions, {
      variables: { input: {} },
    });

  const { data: dashBoardAccessByAccount, loading: isDashBoardAccessByAccountLoading } =
    useQuery<AdminDashboardQueryResponse>(adminExternalDashboardsApi.getDashboardAccountAccess);

  const handleSave = async (formData: ExternalDashboardsPortalConfigFormValues) => {
    const dto = formData.dashboards.map(configItem => ({
      ...configItem,
      accounts: configItem.accounts.map(({ value }) => value),
      accountTypes: Object.entries(configItem.accountTypes).reduce<string[]>((acc, [key, value]) => {
        if (value) acc.push(key);
        return acc;
      }, []),
    }));

    await handleUpdateAccessByAccount(dto);

    // Default landing dashboard is a per-account setting persisted to the ExternalDashboardAccess portal config.
    if (isLenderUser) {
      const existingConfig =
        externalDashboardAccessConfig ??
        getPortalConfigurationDefaultConfig(vendorProfile.id, PortalConfigurationType.ExternalDashboardAccess);
      const existingJsonConfig = existingConfig?.jsonDefinition?.config ?? {};

      const config = {
        dashboardHiddenFor: existingJsonConfig.dashboardHiddenFor ?? {},
        defaultDashboard: {
          enabled: !!formData.defaultDashboardEnabled,
          dashboardId: formData.defaultDashboardEnabled ? formData.defaultDashboardId || null : null,
        },
      };

      await onSaveConfiguration({ ...existingConfig, jsonDefinition: { config } });
    }
  };

  const isLoading = isDashboardListOptionsLoading || isDashBoardAccessByAccountLoading;

  // Only process data when both queries have completed
  const externalDashboardOptions: SelectOption[] = !isLoading
    ? (dashboardsResponse?.externalDashboards?.result?.list ?? [])
    : [];

  const accessByAccountType = !isLoading
    ? (dashBoardAccessByAccount?.externalDashboards?.result?.entity?.byAccountType ?? []).reduce<
        Record<string, string[]>
      >((acc, { accountTypes, dashboardId }) => {
        acc[dashboardId] = accountTypes;
        return acc;
      }, {})
    : {};

  const initialAcc = !isLoading
    ? externalDashboardOptions.reduce<
        Record<DashboardId, { accountTypes: string[]; accounts: SelectOption<AccountId>[] }>
      >((initialAcc, { value }) => {
        initialAcc[value] = { accounts: [], accountTypes: accessByAccountType[value] ?? [] };
        return initialAcc;
      }, {})
    : {};

  const groupedAccountsByDashboard = !isLoading
    ? (dashBoardAccessByAccount?.externalDashboards?.result?.entity?.byAccount ?? []).reduce<
        Record<DashboardId, { accountTypes: string[]; accounts: SelectOption<AccountId>[] }>
      >((acc, item) => {
        if (acc[item.dashboardId]?.accounts && Array.isArray(acc[item.dashboardId].accounts)) {
          acc[item.dashboardId].accounts.push({ value: item.accountId, label: item.accountName });
        }
        return acc;
      }, initialAcc)
    : {};

  const initialValue = !isLoading
    ? Object.entries(groupedAccountsByDashboard).reduce<ExternalDashboardsPortalConfigFormValues>(
        (acc, [dashboardId, { accountTypes, accounts }]) => {
          acc.dashboards.push({
            accounts,
            dashboardId,
            accountTypes: accountTypes.reduce<Record<string, boolean>>(
              (acc, accountType) => {
                acc[accountType] = true;
                return acc;
              },
              { [AccountType.Dealer]: false, [AccountType.Lender]: false, PARTNER: false }
            ),
          });
          return acc;
        },
        { dashboards: [] }
      )
    : { dashboards: [] };

  return (
    <Grid item sm={12} md={6}>
      <CardContainer title="External Dashboards Accounts Access Settings">
        <RenderWithFallback
          isLoading={isLoading}
          fallBack={
            <Stack alignItems="center" justifyContent="center" height="100%">
              <CircularProgress />
            </Stack>
          }
        >
          <ExternalDashboardForm
            onConfirm={handleSave}
            initialValue={initialValue}
            isLoading={isUpdatingAccessByAccount}
            externalDashboardOptions={externalDashboardOptions}
            showDefaultDashboard={isLenderUser}
            defaultDashboardOptions={defaultDashboardOptions}
            defaultDashboardEnabled={defaultDashboardEnabled}
            defaultDashboardId={defaultDashboardId}
          />
        </RenderWithFallback>
      </CardContainer>
    </Grid>
  );
};
